import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { useI18n } from '@/i18n/use-i18n';
import type { Locale } from '@/i18n/translations';
import { userService, type CurrentUserInfo } from '@/services';
import { useThemeStore } from '@/stores';
import './settings.css';

const getStringValue = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const normalizeName = (value: string) => value.replace(/\s+/g, ' ').trim();

type FeedbackState = { type: 'success' | 'error'; message: string } | null;

export function Settings() {
  const { locale, setLocale, strings } = useI18n();
  const { theme, toggle: toggleTheme } = useThemeStore();
  const queryClient = useQueryClient();
  const { data: currentUser } = useQuery<CurrentUserInfo>({
    queryKey: ['current-user'],
    queryFn: userService.getCurrentUser,
    staleTime: 1000 * 60 * 5,
  });

  const name = getStringValue(currentUser?.name);
  const email = getStringValue(currentUser?.email);
  const [editableName, setEditableName] = useState('');
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [languageFeedback, setLanguageFeedback] = useState<FeedbackState>(null);

  useEffect(() => {
    if (!name) return;
    const normalizedStoredName = normalizeName(name);
    setEditableName((current) => (current ? current : normalizedStoredName));
  }, [name]);

  const updateNameMutation = useMutation({
    mutationFn: (nextName: string) => userService.updateName(nextName),
    onSuccess: (data, nextName) => {
      const updatedName = getStringValue(data?.name) || nextName;
      queryClient.setQueryData<CurrentUserInfo>(['current-user'], (previous) => ({
        ...(previous ?? {}),
        ...(typeof data === 'object' && data ? data : {}),
        name: updatedName,
      }));
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setEditableName(normalizeName(updatedName));
      setFeedback({ type: 'success', message: strings.settings.nameUpdated });
    },
    onError: (error) => {
      setFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : strings.settings.nameSaveError,
      });
    },
  });

  const updateLocaleMutation = useMutation({
    mutationFn: (nextLocale: Locale) => userService.updateLocale(nextLocale),
    onMutate: (nextLocale) => {
      setLanguageFeedback(null);
      const previousLocale = locale;
      setLocale(nextLocale);
      return { previousLocale };
    },
    onSuccess: (data, nextLocale) => {
      queryClient.setQueryData<CurrentUserInfo>(['current-user'], (previous) => ({
        ...(previous ?? {}),
        ...(typeof data === 'object' && data ? data : {}),
        locale: nextLocale,
      }));
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setLanguageFeedback({ type: 'success', message: strings.settings.languageSaved });
    },
    onError: (error, _nextLocale, context) => {
      if (context?.previousLocale) {
        setLocale(context.previousLocale);
      }
      setLanguageFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : strings.settings.languageSaveError,
      });
    },
  });

  const normalizedName = normalizeName(editableName);
  const normalizedStoredName = normalizeName(name);
  const hasNameValue = normalizedName.length > 0;
  const hasChanges = normalizedName !== normalizedStoredName;
  const isSaving = updateNameMutation.isPending;
  const canSave = hasNameValue && hasChanges && !isSaving;
  const saveLabel = isSaving ? strings.settings.savingName : strings.settings.saveName;

  const handleNameChange = (value: string) => {
    setEditableName(value);
    if (feedback) setFeedback(null);
  };

  const handleSave = () => {
    if (!canSave) return;
    setFeedback(null);
    updateNameMutation.mutate(normalizedName);
  };

  const handleLocaleChange = (value: string) => {
    if (value === locale) return;
    if (value === 'pt' || value === 'en') {
      updateLocaleMutation.mutate(value);
    }
  };

  return (
    <Layout>
      <section className="settings app-page">
        <header className="settings__header">
          <p className="settings__eyebrow">{strings.settings.eyebrow}</p>
          <h1 className="settings__title">{strings.settings.title}</h1>
          <p className="settings__subtitle">{strings.settings.subtitle}</p>
        </header>

        <div className="settings__card">
          <div className="settings__card-header">
            <div>
              <h2 className="settings__card-title">
                {strings.settings.identificationTitle}
              </h2>
              <p className="settings__card-subtitle">
                {strings.settings.identificationSubtitle}
              </p>
            </div>
            <span className="settings__badge">{strings.settings.emailBadge}</span>
          </div>

          <div className="settings__form">
            <label className="settings__field">
              <span className="settings__label">{strings.settings.nameLabel}</span>
              <input
                className="settings__input"
                type="text"
                value={editableName}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder={strings.settings.namePlaceholder}
                autoComplete="name"
              />
              <span className="settings__helper">{strings.settings.nameHelper}</span>
            </label>

            <label className="settings__field">
              <span className="settings__label">{strings.settings.emailLabel}</span>
              <input
                className="settings__input settings__input--readonly"
                type="email"
                value={email || ''}
                placeholder={strings.settings.emailPlaceholder}
                readOnly
              />
              <span className="settings__helper">{strings.settings.emailHelper}</span>
            </label>
          </div>

          <div className="settings__actions">
            <button
              type="button"
              className="settings__button settings__button--primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saveLabel}
            </button>
            {feedback ? (
              <span
                className={`settings__feedback settings__feedback--${feedback.type}`}
                role={feedback.type === 'error' ? 'alert' : 'status'}
              >
                {feedback.message}
              </span>
            ) : null}
          </div>
        </div>

        <div className="settings__card">
          <div className="settings__card-header">
            <div>
              <h2 className="settings__card-title">{strings.settings.preferencesTitle}</h2>
              <p className="settings__card-subtitle">
                {strings.settings.preferencesSubtitle}
              </p>
            </div>
          </div>

          <div className="settings__form settings__form--single">
            <label className="settings__field">
              <span className="settings__label">{strings.settings.languageLabel}</span>
              <select
                className="settings__input settings__select"
                value={locale}
                onChange={(event) => handleLocaleChange(event.target.value)}
              >
                <option value="pt">{strings.settings.languageOptionPt}</option>
                <option value="en">{strings.settings.languageOptionEn}</option>
              </select>
              <span className="settings__helper">{strings.settings.languageHelper}</span>
              {languageFeedback ? (
                <span
                  className={`settings__feedback settings__feedback--${languageFeedback.type}`}
                  role={languageFeedback.type === 'error' ? 'alert' : 'status'}
                >
                  {languageFeedback.message}
                </span>
              ) : null}
            </label>

            <div className="settings__field">
              <span className="settings__label">{strings.settings.themeLabel}</span>
              <select
                className="settings__input settings__select"
                value={theme}
                onChange={(event) => {
                  if (event.target.value !== theme) toggleTheme();
                }}
              >
                <option value="light">{strings.settings.themeLight}</option>
                <option value="dark">{strings.settings.themeDark}</option>
              </select>
              <span className="settings__helper">{strings.settings.themeHelper}</span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
